import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedTalentSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activePlayer, setActivePlayer] = useState(0);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fallbackPlayers = [
  {
    id: 'ronaldo',
    name: 'Cristiano Ronaldo',
    position: 'Forward',
    age: 40,
    nationality: 'Portugal',
    currentClub: 'Al Nassr',
    clubLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_142111f1d-1767899919848.png",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_142111f1d-1767899919848.png",
    imageAlt: 'Cristiano Ronaldo in action wearing white jersey showing legendary football skills and athletic prowess',
    stats: {
      matches: 1260,
      goals: 960,
      assists: 260,
      rating: 7.5
    },
    marketValue: '€150M',
    highlights: ['5× Ballon d\'Or Winner', '5× UEFA Champions League Winner', 'All-Time Top International Scorer (135 goals for Portugal)', 'Over 915 career goals across all competitions'],
    videoUrl: 'https://example.com/ronaldo.mp4'
  },
  {
    id: 'messi',
    name: 'Lionel Messi',
    position: 'Forward',
    age: 37,
    nationality: 'Argentina',
    currentClub: 'Inter Miami',
    clubLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c19b5f37-1765232066524.png",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_193126077-1764702253366.png",
    imageAlt: 'Lionel Messi in pink Inter Miami jersey dribbling with exceptional ball control and technical mastery',
    stats: {
      matches: 1069,
      goals: 869,
      assists: 377,
      rating: 8.1
    },
    marketValue: '€100M',
    highlights: ['8× Ballon d\'Or Winner', 'FIFA World Cup Champion 2022', 'Record 6× European Golden Shoe Winner'],
    videoUrl: 'https://example.com/messi.mp4'
  },
  {
    id: 'neymar',
    name: 'Neymar Jr',
    position: 'Winger',
    age: 32,
    nationality: 'Brazil',
    currentClub: 'Al Hilal',
    clubLogo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a1098bd2-1764826830512.png',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2865a90-1771336324462.png",
    imageAlt: 'Neymar Jr in blue jersey performing skillful dribble with flair and Brazilian football artistry',
    stats: {
      matches: 752,
      goals: 436,
      assists: 267,
      rating: 7.5
    },
    marketValue: '€40M',
    highlights: ['UEFA Champions League Winner 2015', 'Olympic Gold Medalist 2016', 'Brazil\'s 3rd All-Time Top Scorer'],
    videoUrl: 'https://example.com/neymar.mp4'
  },
  {
    id: 'mbappe',
    name: 'Kylian Mbappé',
    position: 'Forward',
    age: 26,
    nationality: 'France',
    currentClub: 'Real Madrid',
    clubLogo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a1098bd2-1764826830512.png',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_193126077-1764702253366.png",
    imageAlt: 'Kylian Mbappé in white Real Madrid jersey sprinting with explosive speed and dynamic attacking movement',
    stats: {
      matches: 453,
      goals: 327,
      assists: 153,
      rating: 8.4
    },
    marketValue: '€180M',
    highlights: ['FIFA World Cup Winner 2018', 'FIFA World Cup Golden Boot 2022', 'Youngest French Player to Score 300 Goals'],
    videoUrl: 'https://example.com/mbappe.mp4'
  }];


  useEffect(() => {
    fetchFeaturedPlayers();
  }, []);

  const fetchFeaturedPlayers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('player_master_profiles')?.select('*')?.eq('available_for_transfer', true)?.eq('verified_status', true)?.order('created_at', { ascending: false })?.limit(4);

      if (error) throw error;

      if (data && data?.length > 0) {
        const formattedPlayers = data?.map((player) => ({
          id: player?.id,
          name: player?.full_name,
          position: player?.position || 'Player',
          age: player?.age,
          nationality: player?.nationality,
          currentClub: player?.current_team,
          clubLogo: null,
          image: player?.player_photo ? getPlayerImageUrl(player?.player_photo) : "https://img.rocket.new/generatedImages/rocket_gen_img_1f4ab2585-1766847090960.png",
          imageAlt: `${player?.full_name} professional football player in action showing athletic performance and skill`,
          stats: {
            matches: player?.total_appearances || 0,
            goals: player?.goals_scored || 0,
            assists: player?.assists || 0,
            rating: 8.0
          },
          marketValue: player?.market_value || 'N/A',
          highlights: [
          player?.national_team ? `${player?.nationality} National Team` : 'Professional Player',
          player?.competitions || 'League Player',
          `${player?.total_appearances || 0} Career Matches`],

          videoUrl: player?.match_footage || ''
        }));
        setPlayers(formattedPlayers);
      } else {
        setPlayers(fallbackPlayers);
      }
    } catch (error) {
      console.error('Error fetching featured players:', error);
      setPlayers(fallbackPlayers);
    } finally {
      setLoading(false);
    }
  };

  const getPlayerImageUrl = (photoPath) => {
    if (!photoPath) return null;
    const { data } = supabase?.storage?.from('player-media')?.getPublicUrl(photoPath);
    return data?.publicUrl;
  };

  const handlePrevious = () => {
    setActivePlayer((prev) => prev === 0 ? players?.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setActivePlayer((prev) => prev === players?.length - 1 ? 0 : prev + 1);
  };

  if (loading) {
    return (
      <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full mb-6">
              <Icon name="Star" size={18} className="text-amber-600" />
              <span className="text-sm font-bold text-slate-800">{t('homepage.featuredPlayers.eliteTalentShowcase')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4">
              {t('homepage.featuredPlayers.featuredPlayersTitle')}
            </h2>
            <p className="text-slate-600">{t('homepage.featuredPlayers.loadingPlayers')}</p>
          </div>
        </div>
      </section>);

  }

  const currentPlayer = players?.[activePlayer];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full mb-6 shadow-sm">
            <Icon name="Star" size={18} className="text-amber-600" />
            <span className="text-sm font-bold text-slate-800">{t('homepage.featuredPlayers.eliteTalentShowcase')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-amber-600 bg-clip-text text-transparent">
              {t('homepage.featuredPlayers.featuredPlayersTitle')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('homepage.featuredPlayers.discoverExceptional')}
          </p>
        </div>

        {/* Player Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-12">
          {/* Player Image Card */}
          <div className="relative rounded-3xl overflow-hidden h-[450px] md:h-[550px] shadow-2xl group">
            <Image
              src={currentPlayer?.image}
              alt={currentPlayer?.imageAlt}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />

            {/* Top Info Bar */}
            <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
              {/* Market Value Badge */}
              <div className="bg-white/95 backdrop-blur-xl rounded-xl px-4 py-3 shadow-xl">
                <div className="text-xs text-slate-600 font-semibold mb-1">{t('homepage.featuredPlayers.marketValue')}</div>
                <div className="text-xl md:text-2xl font-black bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">{currentPlayer?.marketValue}</div>
              </div>
              
              {/* Video Play Button */}
              {currentPlayer?.videoUrl &&
              <button className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 backdrop-blur-xl rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Icon name="Play" size={24} color="white" />
                </button>
              }
            </div>

            {/* Player Info Bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h3 className="text-3xl md:text-4xl font-black text-white mb-3">{currentPlayer?.name}</h3>
              <div className="flex items-center gap-6 text-base text-white/90">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                  <Icon name="MapPin" size={18} color="white" />
                  <span className="font-semibold">{currentPlayer?.nationality}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                  <Icon name="Calendar" size={18} color="white" />
                  <span className="font-semibold">{currentPlayer?.age} {t('homepage.featuredPlayers.age')}</span>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 shadow-xl hover:scale-110">
              <Icon name="ChevronLeft" size={24} className="text-slate-900" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 shadow-xl hover:scale-110">
              <Icon name="ChevronRight" size={24} className="text-slate-900" />
            </button>
          </div>

          {/* Player Details Card */}
          <div className="flex flex-col justify-between space-y-6">
            {/* Position & Club */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
                <div className="text-sm text-blue-700 font-bold mb-2">{t('homepage.featuredPlayers.position')}</div>
                <div className="text-2xl font-black text-blue-900">{currentPlayer?.position}</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200 rounded-2xl p-6 shadow-lg">
                <div className="text-sm text-emerald-700 font-bold mb-2">{t('homepage.featuredPlayers.currentClub')}</div>
                <div className="text-2xl font-black text-emerald-900">{currentPlayer?.currentClub}</div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 md:p-8 shadow-xl">
              <h4 className="text-lg font-black text-slate-900 mb-6 flex items-center gap-2">
                <Icon name="BarChart3" size={20} className="text-blue-600" />
                {t('homepage.featuredPlayers.keyStats')}
              </h4>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent mb-2">{currentPlayer?.stats?.matches}</div>
                  <div className="text-sm text-slate-600 font-semibold">{t('homepage.featuredPlayers.matches')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-2">{currentPlayer?.stats?.goals}</div>
                  <div className="text-sm text-slate-600 font-semibold">{t('homepage.featuredPlayers.goals')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent mb-2">{currentPlayer?.stats?.assists}</div>
                  <div className="text-sm text-slate-600 font-semibold">{t('homepage.featuredPlayers.assists')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent mb-2">{currentPlayer?.stats?.rating}</div>
                  <div className="text-sm text-slate-600 font-semibold">{t('homepage.featuredPlayers.rating')}</div>
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 rounded-2xl p-6 md:p-8 shadow-lg">
              <h4 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2">
                <Icon name="Trophy" size={20} className="text-amber-600" />
                {t('homepage.featuredPlayers.careerHighlights')}
              </h4>
              <ul className="space-y-3">
                {currentPlayer?.highlights?.map((highlight, idx) =>
                <li key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mt-0.5">
                      <Icon name="Award" size={14} color="white" />
                    </div>
                    <span className="text-slate-700 font-medium">{highlight}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* View Profile Button */}
            <Button
              onClick={() => navigate('/player-profiles')}
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white shadow-xl border-0 text-base font-bold w-full py-6">
              {t('homepage.featuredPlayers.viewFullProfile')}
            </Button>
          </div>
        </div>

        {/* Player Thumbnails */}
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {players?.map((player, index) =>
          <button
            key={player?.id}
            onClick={() => setActivePlayer(index)}
            className={`relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden transition-all duration-300 ${
            activePlayer === index ? 'ring-4 ring-blue-600 scale-110 shadow-xl' : 'opacity-60 hover:opacity-100 hover:scale-105'}`
            }>
              <Image
              src={player?.image}
              alt={player?.imageAlt}
              className="w-full h-full object-cover" />
              {activePlayer === index &&
            <div className="absolute inset-0 bg-blue-600/20" />
            }
            </button>
          )}
        </div>

        {/* View All Button */}
        <div className="mt-12 text-center">
          <Button
            onClick={() => navigate('/player-profiles')}
            variant="outline"
            size="lg"
            iconName="Users"
            iconPosition="left"
            className="border-2 border-slate-300 text-slate-900 hover:bg-slate-100 font-bold px-10 py-6">
            {t('homepage.featuredPlayers.viewAllPlayers')}
          </Button>
        </div>
      </div>
    </section>);

};

export default FeaturedTalentSection;